import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { PersonEvent, MinisterialActivityArea } from '../models/person-event.model';

interface MovementUnit { id?: string; name: string; }
export interface PersonEventMovement {
  label: string;
  activityName?: string;
  from: MovementUnit[];
  to: MovementUnit[];
}

export function buildPersonEventMovements(event: PersonEvent): PersonEventMovement[] {
  if (event.eventTypeKey !== 'AppointmentAssignment') return [];
  const changes = event.generatedActivityChanges ?? [];
  const movements: PersonEventMovement[] = [];
  const unit = (area: MinisterialActivityArea): MovementUnit => ({ id: area.organizationUnitId, name: area.organizationUnitName });
  const key = (value: MovementUnit) => value.id?.toLowerCase() || value.name;
  const unique = (values: MovementUnit[]) => [...new Map(values.map(value => [key(value), value])).values()];
  const roles = new Map<string, MinisterialActivityArea[]>();
  const displayedDetail = event.detail?.trim().toLocaleLowerCase('es');
  for (const change of changes.filter(change => {
    const referenceName = change.referenceName.trim().toLocaleLowerCase('es');
    return change.kind !== 'Ministry' &&
      !(displayedDetail && referenceName && displayedDetail.includes(referenceName));
  })) {
    const roleKey = `${change.kind}:${change.referenceId}:${change.changeType}`;
    roles.set(roleKey, [...(roles.get(roleKey) ?? []), change]);
  }
  for (const group of roles.values()) {
    const first = group[0];
    const label = first.kind === 'Appointment' ? 'Encargo' : 'Nombramiento';
    movements.push({
      label: first.changeType === 'Added' ? label : `Finalización de ${label.toLowerCase()}`,
      activityName: first.referenceName,
      from: [], to: unique(group.map(unit)),
    });
  }
  const ministryIds = new Set(changes.filter(change => change.kind === 'Ministry').map(change => change.referenceId));
  for (const ministryId of ministryIds) {
    const group = changes.filter(change => change.kind === 'Ministry' && change.referenceId === ministryId);
    const added = unique(group.filter(change => change.changeType === 'Added').map(unit));
    const ended = unique(group.filter(change => change.changeType === 'Ended').map(unit));
    const from = ended.filter(value => !added.some(other => key(other) === key(value)));
    const to = added.filter(value => !ended.some(other => key(other) === key(value)));
    if (!from.length && !to.length) continue;
    const action = from.length && to.length ? 'Reasignación' : to.length ? 'Ampliación de área' : 'Finalización de actividad';
    movements.push({ label: `${action} de ${group[0].referenceName}`, from: to.length ? from : [], to: to.length ? to : from });
  }
  return movements;
}

@Component({
  selector: 'app-person-event-effects',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <ng-container *ngIf="movements.length">
      <details *ngIf="collapsible; else expandedContent" class="event-effects" (click)="$event.stopPropagation()">
        <summary>Ver movimientos · {{ movements.length }}</summary>
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </details>
      <ng-template #expandedContent><ng-container *ngTemplateOutlet="content"></ng-container></ng-template>
      <ng-template #content>
        <dl class="event-movements" aria-label="Movimientos generados por el acto">
          <div *ngFor="let movement of movements" class="event-movements__row">
            <dt>{{ movement.label }}</dt>
            <dd>
              <span *ngIf="movement.activityName">{{ movement.activityName }} en </span>
              <ng-container *ngFor="let value of movement.from; let last = last">
                <ng-container *ngTemplateOutlet="unit; context: { $implicit: value }"></ng-container><span *ngIf="!last">, </span>
              </ng-container>
              <span *ngIf="movement.from.length" class="event-movements__arrow" aria-label="hacia">→</span>
              <ng-container *ngFor="let value of movement.to; let last = last">
                <ng-container *ngTemplateOutlet="unit; context: { $implicit: value }"></ng-container><span *ngIf="!last">, </span>
              </ng-container>
            </dd>
          </div>
        </dl>
      </ng-template>
      <ng-template #unit let-value>
        <a *ngIf="value.id; else plainUnit" [routerLink]="['/units', value.id]">{{ value.name }}</a>
        <ng-template #plainUnit>{{ value.name }}</ng-template>
      </ng-template>
    </ng-container>
  `,
  styles: [`
    :host { display: block; min-width: 0; }
    .event-effects { margin-top: 6px; font-size: 12px; line-height: 1.5; }
    summary { width: fit-content; cursor: pointer; color: #35658e; font-weight: 500; }
    summary:focus-visible { outline: 2px solid #357dc0; outline-offset: 3px; border-radius: 2px; }
    .event-movements { display: grid; gap: 10px; margin: 0; padding: 16px 24px; color: #244560; font-size: 13px; line-height: 20px; white-space: normal; }
    .event-movements__row { display: grid; grid-template-columns: minmax(160px, 220px) minmax(0, 1fr); gap: 16px; }
    dt { color: #687c8e; font-weight: 500; }
    dd { margin: 0; overflow-wrap: anywhere; }
    .event-movements__arrow { padding-inline: 8px; color: #748595; }
    .event-effects .event-movements { padding: 12px 0 0; }
    a { color: #2373bb; text-decoration: none; }
    a:hover { text-decoration: underline; }
    @media (max-width: 650px) {
      .event-movements { padding: 12px; }
      .event-movements__row { grid-template-columns: 1fr; gap: 2px; }
    }
  `],
})
export class PersonEventEffectsComponent {
  @Input({ required: true }) event!: PersonEvent;
  @Input() collapsible = true;
  get movements(): PersonEventMovement[] { return buildPersonEventMovements(this.event); }
}
